import React, { useState, useEffect } from "react"
import {
	Modal,
	Input,
	Avatar,
	Spin,
	Radio,
	Button,
	Popconfirm,
	Switch,
} from "antd"
import {
	EditOutlined,
	RobotOutlined,
	SearchOutlined,
	InboxOutlined,
} from "@ant-design/icons"
import {
	updateGroup,
	updateNotifPref,
	archiveGroup,
	Group,
	GroupMember,
} from "firebase/groupsService"
import { API, Endpoints } from "utils/api"
import { getInitials } from "../utils"
import { Driver } from "../types"
import strings from "utils/strings"

interface EditGroupModalProps {
	visible: boolean
	selectedGroup: Group
	loggedUserId: string
	onClose: () => void
	onArchived: () => void
}

export const EditGroupModal: React.FC<EditGroupModalProps> = ({
	visible,
	selectedGroup,
	loggedUserId,
	onClose,
	onArchived,
}) => {
	const [editName, setEditName] = useState("")
	const [editDesc, setEditDesc] = useState("")
	const [editMembers, setEditMembers] = useState<GroupMember[]>([])
	const [hasBotActive, setHasBotActive] = useState(false)
	const [notifPref, setNotifPref] = useState<"all" | "mentions" | "none">("all")
	const [driverSearch, setDriverSearch] = useState("")
	const [drivers, setDrivers] = useState<Driver[]>([])
	const [loadingDrivers, setLoadingDrivers] = useState(false)
	const [saving, setSaving] = useState(false)
	const [archiving, setArchiving] = useState(false)

	useEffect(() => {
		if (!visible || !selectedGroup) return
		setEditName(selectedGroup.name)
		setEditDesc(selectedGroup.description || "")
		setHasBotActive(selectedGroup.hasBotActive)
		setEditMembers(selectedGroup.members.filter((m) => m.type !== "bot"))
		setNotifPref(
			(selectedGroup.notifPrefs?.[loggedUserId] ?? "all") as
				| "all"
				| "mentions"
				| "none"
		)
		setDriverSearch("")
		loadDrivers()
	}, [visible, selectedGroup, loggedUserId])

	const loadDrivers = async () => {
		setLoadingDrivers(true)
		try {
			const res = await API.get({ url: Endpoints.uriStaff() })
			if (res.ok) {
				// Show all staff so backoffice users are also manageable
				const all: Driver[] = res.data?.results?.staff || []
				setDrivers(all.filter((s) => s.role === "driver" && s.isActive))
			}
		} finally {
			setLoadingDrivers(false)
		}
	}

	const memberType = (role: string): "driver" | "backoffice" =>
		role === "driver" ? "driver" : "backoffice"

	const roleLabel = (role: string) => {
		const map: Record<string, string> = {
			driver: strings.staff.driver || role,
			admin: strings.staff.admin || role,
			owner: strings.staff.owner || role,
			manager: strings.staff.manager || role,
			supervisor: strings.staff.supervisor || role,
			dispatcher: strings.staff.dispatcher || role,
			operator: strings.staff.operator || role,
			operations: strings.staff.operator || role,
		}
		return map[role] ?? role
	}

	const toggleDriver = (driver: Driver) => {
		if (driver.firebaseUid === loggedUserId) return // never remove yourself
		const exists = editMembers.find((m) => m.id === driver.firebaseUid)
		if (exists) {
			setEditMembers((prev) => prev.filter((m) => m.id !== driver.firebaseUid))
		} else {
			setEditMembers((prev) => [
				...prev,
				{
					id: driver.firebaseUid,
					name: driver.name,
					type: memberType(driver.role),
				},
			])
		}
	}

	const handleArchive = async () => {
		setArchiving(true)
		try {
			await archiveGroup(selectedGroup.id)
			onClose()
			onArchived()
		} finally {
			setArchiving(false)
		}
	}

	const handleSave = async () => {
		if (!editName.trim()) return
		setSaving(true)
		try {
			await Promise.all([
				updateGroup(selectedGroup.id, {
					name: editName.trim(),
					description: editDesc.trim(),
					members: editMembers,
					hasBotActive,
				}),
				updateNotifPref(selectedGroup.id, loggedUserId, notifPref),
			])
			onClose()
		} finally {
			setSaving(false)
		}
	}

	const filteredDrivers = drivers.filter(
		(d) =>
			d.name.toLowerCase().includes(driverSearch.toLowerCase()) ||
			d.email.toLowerCase().includes(driverSearch.toLowerCase())
	)

	return (
		<Modal
			title={
				<div className="modal-title">
					<EditOutlined style={{ marginRight: 8, color: "#2B6CF4" }} />
					{strings.messaging.editGroup}
				</div>
			}
			visible={visible}
			onCancel={onClose}
			width={520}
			centered
			className="create-group-modal"
			bodyStyle={{
				padding: "16px 24px",
				maxHeight: "calc(100vh - 220px)",
				overflowY: "auto",
			}}
			footer={
				<div className="cg-footer">
					<Popconfirm
						title={strings.messaging.archiveGroupConfirm}
						onConfirm={handleArchive}
						okText={strings.messaging.archiveGroup}
						cancelText={strings.generic.cancel}
						okButtonProps={{ danger: true, loading: archiving }}
						placement="topLeft"
					>
						<Button danger icon={<InboxOutlined />} disabled={saving}>
							{strings.messaging.archiveGroup}
						</Button>
					</Popconfirm>
					<div className="cg-footer__actions">
						<Button onClick={onClose} disabled={saving}>
							{strings.generic.cancel}
						</Button>
						<Button
							type="primary"
							onClick={handleSave}
							loading={saving}
							disabled={!editName.trim()}
						>
							{strings.generic.save}
						</Button>
					</div>
				</div>
			}
		>
			{/* Name */}
			<div className="cg-section">
				<div className="cg-label">{strings.messaging.groupNameRequired}</div>
				<Input
					value={editName}
					onChange={(e) => setEditName(e.target.value)}
					maxLength={60}
				/>
			</div>

			{/* Description */}
			<div className="cg-section">
				<div className="cg-label">
					{strings.fields.description}{" "}
					<span className="cg-optional">{strings.messaging.optional}</span>
				</div>
				<Input
					value={editDesc}
					onChange={(e) => setEditDesc(e.target.value)}
					maxLength={120}
				/>
			</div>

			{/* Bot toggle */}
			<div className="cg-section">
				<div className="cg-bot-notice">
					<RobotOutlined style={{ color: "#F59E0B", fontSize: 16 }} />
					<span className="cg-bot-notice__text">
						<strong>Dingoo Bot</strong>
						<span className="cg-bot-notice__desc">
							{strings.messaging.botToggleDescription}
						</span>
					</span>
					<Switch
						checked={hasBotActive}
						onChange={setHasBotActive}
						size="small"
					/>
				</div>
			</div>

			{/* Notification preferences */}
			<div className="cg-section">
				<div className="cg-label">{strings.messaging.notifPrefTitle}</div>
				<Radio.Group
					value={notifPref}
					onChange={(e) => setNotifPref(e.target.value)}
					style={{ display: "flex", flexDirection: "column", gap: 6 }}
				>
					<Radio value="all">{strings.messaging.notifAll}</Radio>
					<Radio value="mentions">{strings.messaging.notifMentions}</Radio>
					<Radio value="none">{strings.messaging.notifNone}</Radio>
				</Radio.Group>
			</div>

			{/* Staff list — check = in group, uncheck = remove */}
			<div className="cg-section" style={{ marginBottom: 0 }}>
				<div className="cg-label">
					{strings.messaging.membersLabel}
					<span className="cg-count">
						{editMembers.length} {strings.messaging.inGroup}
					</span>
				</div>
				<Input
					prefix={<SearchOutlined />}
					placeholder={strings.messaging.searchMember}
					value={driverSearch}
					onChange={(e) => setDriverSearch(e.target.value)}
					style={{ marginBottom: 6 }}
				/>
				<div className="driver-list">
					{loadingDrivers ? (
						<div className="driver-list__loading">
							<Spin size="small" />
						</div>
					) : filteredDrivers.length === 0 ? (
						<div className="driver-list__empty">
							{driverSearch
								? strings.generic.noResults
								: strings.messaging.noMemberFound}
						</div>
					) : (
						filteredDrivers.map((driver) => {
							const checked = Boolean(
								editMembers.find((m) => m.id === driver.firebaseUid)
							)
							const isMe = driver.firebaseUid === loggedUserId
							return (
								<div
									key={driver.firebaseUid}
									className={`driver-row${checked ? " driver-row--checked" : ""}${isMe ? " driver-row--disabled" : ""}`}
									onClick={() => !isMe && toggleDriver(driver)}
								>
									<div
										className={`driver-row__check${checked ? " driver-row__check--on" : ""}`}
									>
										{checked && <span>✓</span>}
									</div>
									<Avatar
										size={28}
										style={{
											background: isMe ? "#2B6CF4" : "#2C3E50",
											fontSize: 11,
											flexShrink: 0,
										}}
									>
										{getInitials(driver.name)}
									</Avatar>
									<div className="driver-row__info">
										<span className="driver-row__name">
											{driver.name}
											{isMe && (
												<span
													style={{
														color: "#A0AEC0",
														fontWeight: 400,
														marginLeft: 6,
													}}
												>
													{strings.messaging.you}
												</span>
											)}
										</span>
										<span className="driver-row__email">
											{roleLabel(driver.role)} · {driver.email}
										</span>
									</div>
								</div>
							)
						})
					)}
				</div>
			</div>
		</Modal>
	)
}
